import { reactive, computed } from 'vue'
import type { SearchResult, SearchRequest, SearchHistory } from '../types/search'
import { apiClient } from '../services/apiClient'
import { searchHistoryManager } from '../utils/searchHistoryManager'

interface UseKeywordSearchState {
  selectedVersion: string | null
  results: SearchResult[]
  totalCount: number
  isLoading: boolean
  isLoadingMore: boolean
  error: string | null
  currentOffset: number
  allResultsLoaded: boolean
  searchHistory: SearchHistory[]
  searchQuery: string
  isRegex: boolean
  pageSize: number
}

export function useKeywordSearch() {
  const state = reactive<UseKeywordSearchState>({
    selectedVersion: null,
    results: [],
    totalCount: 0,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    currentOffset: 0,
    allResultsLoaded: false,
    searchHistory: [],
    searchQuery: '',
    isRegex: false,
    pageSize: 200,
  })

  // 計算屬性
  const hasResults = computed(() => state.results.length > 0)
  const isEmpty = computed(() => !state.isLoading && !state.error && state.results.length === 0)

  /**
   * 選擇聖經譯本
   */
  const selectVersion = (versionCode: string): void => {
    state.selectedVersion = versionCode
    state.results = []
    state.totalCount = 0
    state.error = null
    state.currentOffset = 0
    state.isLoadingMore = false
    state.allResultsLoaded = false
    loadSearchHistory(versionCode)
  }

  /**
   * 載入搜尋歷史
   */
  const loadSearchHistory = (versionCode: string): void => {
    state.searchHistory = searchHistoryManager.getHistory(versionCode)
  }

  /**
   * 執行初始搜尋（第一頁）
   */
  const search = async (query: string, versionCode: string | null, isRegex: boolean = false): Promise<void> => {
    if (!query || !versionCode) {
      state.error = '請輸入搜尋關鍵字並選擇聖經版本'
      return
    }

    state.isLoading = true
    state.isLoadingMore = false
    state.error = null
    state.currentOffset = 0
    state.allResultsLoaded = false
    state.searchQuery = query
    state.isRegex = isRegex
    state.results = []

    try {
      const request: SearchRequest = {
        query,
        versionCode,
        isRegex,
        offset: 0,
        limit: state.pageSize,
      }

      const response = await apiClient.keywordSearch(request)

      if (!response.success || !response.data) {
        throw new Error(response.error || '搜尋失敗')
      }

      state.results = response.data.results
      state.totalCount = response.data.total
      state.currentOffset = 0

      // 檢查是否已載入全部結果
      // 若返回的結果數 < limit，表示已到達最後一頁
      if (response.data.results.length < state.pageSize) {
        state.allResultsLoaded = true
      }

      // 保存搜尋歷史
      searchHistoryManager.save({
        query,
        versionCode,
        resultCount: response.data.total,
        timestamp: Date.now(),
        isRegex,
      })

      // 重新載入歷史
      loadSearchHistory(versionCode)
    } catch (error) {
      state.error = error instanceof Error ? error.message : '發生未知錯誤'
      state.results = []
      state.totalCount = 0
    } finally {
      state.isLoading = false
    }
  }

  /**
   * 載入下一頁搜尋結果（無限捲軸）
   */
  const loadMore = async (): Promise<void> => {
    // 檢查是否已全部載入或正在載入
    if (
      state.allResultsLoaded ||
      state.isLoadingMore ||
      state.isLoading ||
      !state.selectedVersion ||
      !state.searchQuery
    ) {
      return
    }

    state.isLoadingMore = true
    const nextOffset = state.currentOffset + state.pageSize

    try {
      const request: SearchRequest = {
        query: state.searchQuery,
        versionCode: state.selectedVersion,
        isRegex: state.isRegex,
        offset: nextOffset,
        limit: state.pageSize,
      }

      const response = await apiClient.keywordSearch(request)

      if (!response.success || !response.data) {
        throw new Error(response.error || '載入失敗')
      }

      // 追加新結果
      state.results.push(...response.data.results)
      state.currentOffset = nextOffset

      // 檢查是否已載入全部結果
      // 若返回的結果數 < limit，表示已到達最後一頁
      const returnedCount = response.data.results.length
      if (returnedCount < state.pageSize) {
        state.allResultsLoaded = true
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : '載入更多結果時發生錯誤'
    } finally {
      state.isLoadingMore = false
    }
  }

  /**
   * 從歷史選擇並搜尋
   */
  const searchFromHistory = async (history: SearchHistory): Promise<void> => {
    await search(history.query, history.versionCode, history.isRegex)
  }

  /**
   * 清除搜尋歷史
   */
  const clearHistory = (versionCode: string): void => {
    searchHistoryManager.clearHistory(versionCode)
    loadSearchHistory(versionCode)
  }

  /**
   * 清除搜尋結果
   */
  const clearResults = (): void => {
    state.results = []
    state.totalCount = 0
    state.error = null
    state.currentOffset = 0
    state.isLoadingMore = false
    state.allResultsLoaded = false
  }

  /**
   * 取得相對時間
   */
  const getRelativeTime = (timestamp: number): string => {
    const now = Date.now()
    const diff = now - timestamp
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (seconds < 60) return '少於 1 分鐘'
    if (minutes < 60) return `${minutes} 分鐘前`
    if (hours < 24) return `${hours} 小時前`
    if (days < 7) return `${days} 天前`

    const date = new Date(timestamp)
    return date.toLocaleDateString('zh-TW')
  }

  return {
    // State
    state,
    selectedVersion: computed(() => state.selectedVersion),
    results: computed(() => state.results),
    totalCount: computed(() => state.totalCount),
    isLoading: computed(() => state.isLoading),
    isLoadingMore: computed(() => state.isLoadingMore),
    error: computed(() => state.error),
    currentOffset: computed(() => state.currentOffset),
    allResultsLoaded: computed(() => state.allResultsLoaded),
    searchHistory: computed(() => state.searchHistory),
    searchQuery: computed(() => state.searchQuery),
    isRegex: computed(() => state.isRegex),
    pageSize: computed(() => state.pageSize),
    // Computed
    hasResults,
    isEmpty,
    // Methods
    selectVersion,
    loadSearchHistory,
    search,
    searchFromHistory,
    loadMore,
    clearHistory,
    clearResults,
    getRelativeTime,
  }
}
